import React, { useState } from 'react';
import { McRadioGroup } from "@maersk-global/mds-react-wrapper/components-core/mc-radio-group";
import { McRadio } from "@maersk-global/mds-react-wrapper/components-core/mc-radio";
import { McButton, McIcon, McTooltip, McSelect } from '@maersk-global/mds-react-wrapper';
import { TagFilterChips } from './TagFilterChips';
import { QuerySearchInput } from './QuerySearchInput';
import { ApplicationOption } from '../../hooks/useApplicationAccess';

interface QueryPageHeaderProps {
    searchQuery?: string;
    setSearchQuery?: (value: string) => void;
    tagSearchQuery: string;
    setTagSearchQuery: (value: string) => void;
    statusFilter: 'all' | 'active' | 'inactive';
    setStatusFilter: (value: 'all' | 'active' | 'inactive') => void;
    favoriteOnly?: boolean;
    setFavoriteOnly?: (value: boolean) => void;
    selectedTags?: string[];
    setSelectedTags?: (tags: string[]) => void;
    selectedApplicationId?: string | null;
    setSelectedApplicationId?: (id: string) => void;
    accessibleApplications?: ApplicationOption[];
    onNavigate: (path: string) => void;
    onOpenSchemaExplorer?: () => void;
}

export const QueryPageHeader: React.FC<QueryPageHeaderProps> = ({
    searchQuery = '',
    setSearchQuery,
    tagSearchQuery,
    setTagSearchQuery,
    statusFilter,
    setStatusFilter,
    favoriteOnly = false,
    setFavoriteOnly,
    selectedTags = [],
    setSelectedTags,
    selectedApplicationId,
    setSelectedApplicationId,
    accessibleApplications = [],
    onNavigate,
    onOpenSchemaExplorer,
}) => {
    const [showTagFilter, setShowTagFilter] = useState(false);

    return (
        <div style={{ marginBottom: '24px' }}>
            <div style={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: '12px',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid var(--mds-color-neutral-weak-decoration, #e0e0e0)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                {/* Left group: all filter controls on one non-wrapping row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap', flex: '1 1 auto', overflow: 'hidden' }}>
                    <div style={{ flexShrink: 0 }}>
                        <McRadioGroup
                            legend="Query status filter"
                            hiddenlegend
                            orientation="horizontal"
                            name="query-status"
                            value={statusFilter}
                            change={(e: any) => {
                                const newValue = e.detail;
                                if (newValue) setStatusFilter(newValue);
                            }}
                        >
                            <McRadio name="query-status" value="all" label="All" checked={statusFilter === 'all'} />
                            <McRadio name="query-status" value="active" label="Active" checked={statusFilter === 'active'} />
                            <McRadio name="query-status" value="inactive" label="Inactive" checked={statusFilter === 'inactive'} />
                        </McRadioGroup>
                    </div>

                    {/* Application dropdown — shown when user has access to >1 app */}
                    {accessibleApplications.length > 1 && setSelectedApplicationId && (
                        <div style={{ flexShrink: 0, minWidth: '160px', maxWidth: '220px' }}>
                            <McSelect
                                label="Application"
                                hiddenlabel
                                placeholder="All applications"
                                value={selectedApplicationId || ''}
                                data={accessibleApplications.map(app => ({
                                    label: app.name || app.applicationId,
                                    value: app.applicationId,
                                }))}
                                optionselected={(e: any) => setSelectedApplicationId(e.detail?.value ?? '')}
                            />
                        </div>
                    )}

                    {/* Search — grows to fill remaining space, scoped by selected application */}
                    {setSearchQuery && (
                        <div style={{ flex: '1 1 0', minWidth: '160px' }}>
                            <QuerySearchInput
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder="Search by ID or name..."
                            />
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                        {setFavoriteOnly && (
                            <McButton
                                variant={favoriteOnly ? 'filled' : 'outlined'}
                                appearance={favoriteOnly ? 'primary' : 'neutral'}
                                icon="star"
                                click={() => setFavoriteOnly(!favoriteOnly)}
                                style={favoriteOnly ? {
                                    '--mc-button-primary-background-color': '#FF6400',
                                    '--mc-button-primary-background-color-hover': '#F56C00',
                                    '--mc-button-primary-border-color': '#FF6400',
                                    backgroundColor: '#FF6400',
                                    borderColor: '#FF6400'
                                } as any : {}}
                            >
                                {favoriteOnly ? 'Favorites' : 'All'}
                            </McButton>
                        )}

                        {setSelectedTags && (
                            <McButton
                                variant={showTagFilter ? 'filled' : 'outlined'}
                                appearance={selectedTags.length > 0 ? 'primary' : 'neutral'}
                                icon="tag"
                                click={() => setShowTagFilter(!showTagFilter)}
                            >
                                Tags {selectedTags.length > 0 && `(${selectedTags.length})`}
                            </McButton>
                        )}
                    </div>
                </div>

                {/* Right group: action buttons */}
                <div style={{ display: 'flex', gap: '12px', flexShrink: 0, alignItems: 'center' }}>
                    <McTooltip>
                        <McButton
                            slot="trigger"
                            appearance="secondary"
                            variant="outlined"
                            onClick={() => onNavigate('/queries/create')}
                        >
                            <McIcon icon="plus" slot="prefix" />
                            New Query
                        </McButton>
                        <span slot="content">
                            Create a new SQL query
                        </span>
                    </McTooltip>

                    {onOpenSchemaExplorer && (
                        <McButton
                            appearance="secondary"
                            variant="outlined"
                            onClick={onOpenSchemaExplorer}
                        >
                            <McIcon icon="database" slot="prefix" />
                            Schema Explorer
                        </McButton>
                    )}
                </div>
            </div>

            {showTagFilter && setSelectedTags && (
                <div style={{ marginTop: '16px' }}>
                    <TagFilterChips
                        selectedTags={selectedTags}
                        onTagsChange={setSelectedTags}
                        applicationId={selectedApplicationId}
                    />
                </div>
            )}
        </div>
    );
};
